import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ProductsService from '../services/ProductsService';

// Query keys
export const productsKeys = {
    all: ['products'] as const,
    lists: () => [...productsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...productsKeys.lists(), { filters }] as const,
    details: () => [...productsKeys.all, 'detail'] as const,
    detail: (id: string) => [...productsKeys.details(), id] as const,
};

// Hooks
export const useProductsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: productsKeys.list(filters),
        queryFn: () => ProductsService.fetchRecords(filters),
    });
};

export const useProductQuery = (id: string) => {
    return useQuery({
        queryKey: productsKeys.detail(id),
        queryFn: () => ProductsService.fetch(id),
        enabled: !!id,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useProductsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
